/**
 * YouTube URL validation utility
 */

/**
 * Validate if URL is a valid YouTube URL
 * @param url - The URL to validate
 * @returns true if valid YouTube URL, false otherwise
 */
export function isValidYouTubeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmedUrl = url.trim();
  
  if (trimmedUrl === '') {
    return false;
  }

  // YouTube URL patterns - support all YouTube video formats:
  // https://www.youtube.com/watch?v=VIDEO_ID
  // https://youtu.be/VIDEO_ID
  // https://m.youtube.com/watch?v=VIDEO_ID
  // https://youtube.com/watch?v=VIDEO_ID
  // https://www.youtube.com/shorts/VIDEO_ID (YouTube Shorts)
  // https://www.youtube.com/embed/VIDEO_ID (Embed URLs)
  // https://www.youtube.com/v/VIDEO_ID (Legacy format)
  // http:// versions are also supported
  
  const youtubePatterns = [
    /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]+/i,
    /^https?:\/\/youtu\.be\/[\w-]+/i,
    /^https?:\/\/m\.youtube\.com\/watch\?v=[\w-]+/i,
    /^https?:\/\/(www\.)?youtube\.com\/shorts\/[\w-]+/i,
    /^https?:\/\/(www\.)?youtube\.com\/embed\/[\w-]+/i,
    /^https?:\/\/(www\.)?youtube\.com\/v\/[\w-]+/i
  ];

  return youtubePatterns.some(pattern => pattern.test(trimmedUrl));
}

/**
 * Get YouTube video ID from URL
 * @param url - The YouTube URL
 * @returns video ID or null if invalid
 */
export function getYouTubeVideoId(url: string): string | null {
  if (!isValidYouTubeUrl(url)) {
    return null;
  }

  const trimmedUrl = url.trim();
  
  // Extract video ID from different YouTube URL formats
  const patterns = [
    /[?&]v=([^&]+)/, // youtube.com/watch?v=VIDEO_ID
    /youtu\.be\/([^?&]+)/, // youtu.be/VIDEO_ID
    /m\.youtube\.com\/watch\?v=([^&]+)/, // m.youtube.com/watch?v=VIDEO_ID
    /youtube\.com\/shorts\/([^?&]+)/, // youtube.com/shorts/VIDEO_ID
    /youtube\.com\/embed\/([^?&]+)/, // youtube.com/embed/VIDEO_ID
    /youtube\.com\/v\/([^?&]+)/ // youtube.com/v/VIDEO_ID
  ];

  for (const pattern of patterns) {
    const match = trimmedUrl.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Get YouTube embed URL from video URL
 * @param url - The YouTube URL
 * @returns embed URL or null if invalid
 */
export function getYouTubeEmbedUrl(url: string): string | null {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) {
    return null;
  }

  return `https://www.youtube.com/embed/${videoId}`;
}

/**
 * Get YouTube thumbnail URL from video URL
 * @param url - The YouTube URL
 * @returns thumbnail URL or null if invalid
 */
export function getYouTubeThumbnailUrl(url: string): string | null {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) {
    return null;
  }

  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}
